import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Headphones, MessageCircle, Phone, Gift, Lock, Radio,
  Flag, MoreHorizontal, Shield, ShieldOff, Tag,
} from 'lucide-react';
import { Button } from '../ui/button';
import Avatar from '../Avatar';
import { AudioAvatarPlayer } from '../AudioAvatarPlayer';
import BackButton from '../BackButton';
import CallCreatorSection from '../o2ocall/CallCreatorSection';
import { CallAvailabilityButton } from '../o2ocall/CallAvailabilityButton';
import ChatTipDialog from '../messages/ChatTipDialog';
import ReportUserDialog from '../ReportUserDialog';
import { ProfileShareMenuItems } from '../ProfileShareMenuItems';
import { VoceProBadge } from '../messages/ContactsPane';
import RaccontiSection from './RaccontiSection';
import VetrinaLiveSection from './VetrinaLiveSection';
import { ProfileLiveSchedule } from '../ProfileLiveSchedule';
import VoiceTabsBar from './VoiceTabsBar';
import CategoriesTab from './CategoriesTab';
import { toggleFollow } from '../../api/social';
import { useToast } from '../../hooks/use-toast';
import { langFlag, langLabel } from '../../lib/creatorMeta';
import { useCategories } from '../../api/categories';

/**
 * VetrinaProfile — Phase F5. Voice-First public profile ("business card
 * estesa"): identity = @username only, no social metrics, no grid.
 * REUSES: Follow→Preferiti, F4 chat + chat-tip system, CallCreatorSection
 * (O2O), Live system/cards, private-profile access flow, AudioAvatarPlayer.
 */
const VetrinaProfile = ({
  profile, setProfile, me, posts, lives,
  locked, accessInfo, accessBusy, onRequestAccess, onCancelRequest,
  blockStatus, onToggleBlock, blockBusy,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // ChatTipDialog reuses the F4 messages i18n namespace (tip.* keys).
  const { t } = useTranslation('messages');
  const [tipOpen, setTipOpen] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  // Voice-First profile menu: 🎙 | 📖 | 🔴 | 🏷
  const [tab, setTab] = useState('audio');
  const o2oRef = useRef(null);

  const isVocePro = !!profile.is_verified_creator;
  // Phase F5.2 — public macro-category + voluntary gender (null-safe).
  const { nameOf } = useCategories();
  // Same display default as the People card (feed.py DEFAULT_VOCE_PRO_CATEGORY): never written to DB.
  const categoryName = nameOf(profile.category_slug) || (isVocePro ? nameOf('generale') : '');
  const secondaryCats = (profile.secondary_category_slugs || []).map((s) => ({ slug: s, name: nameOf(s) })).filter((c) => c.name);
  const genderText = profile.gender ? ({ male: 'Uomo', female: 'Donna', other: 'Altro' }[profile.gender] || '') : '';
  const liveNow = lives.find((l) => l.status === 'live');
  const scheduledLives = lives.filter((l) => ['scheduled', 'waiting_tracks'].includes(l.status));
  const pastLives = lives.filter((l) => l.status === 'ended');
  const accessPrice = accessInfo?.price_credits || 0;
  const onLiveChange = (updated) => { /* LiveCard local refresh handled by parent polling */ };

  const toggleFav = async () => {
    if (!me) { navigate('/login'); return; }
    try {
      const res = await toggleFollow(profile.username);
      setProfile({ ...profile, is_following: res.following });
    } catch (_e) {
      toast({ title: 'Operazione non riuscita', variant: 'destructive' });
    }
  };

  const scrollToO2O = () => {
    o2oRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pb-24 pt-5 md:pt-8" data-testid="vetrina-profile">
      <BackButton className="mb-4" />

      {/* ── HEADER / IDENTITÀ ─────────────────────────────────── */}
      <header className="flex items-start gap-4" data-testid="vetrina-header">
        <div className="relative shrink-0">
          <div className={liveNow ? 'rounded-full ring-2 ring-red-500 ring-offset-2 ring-offset-background' : ''}>
            <Avatar src={profile.avatar_url} alt={profile.username} size={92} data-testid="vetrina-avatar" />
          </div>
          {/* LIVE ORA > ONLINE > OFFLINE (same semantics as PersonCard) */}
          {!liveNow && (
            <span
              className={`absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full border-2 border-background ${
                profile.is_online ? 'bg-emerald-500' : 'bg-neutral-600'
              }`}
              data-testid={`vetrina-presence-${profile.is_online ? 'online' : 'offline'}`}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-lg font-bold" data-testid="vetrina-username">@{profile.username}</h1>
            {isVocePro && <VoceProBadge username={profile.username} label="Voce Pro" />}
            {profile.is_private && <Lock className="h-4 w-4 gold-text" data-testid="vetrina-private-lock" />}
            {liveNow && (
              <span className="flex items-center gap-1 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="vetrina-liveora-badge">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live ora
              </span>
            )}
            {profile.is_private && accessInfo?.my_status === 'approved' && (
              <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider gold-text" data-testid="vetrina-member-badge">
                Membro
              </span>
            )}
            {/* ⋯ block menu (existing block system) */}
            {me && me.username !== profile.username && (
              <div className="relative ml-auto">
                <button
                  onClick={() => setShowActions((s) => !s)}
                  className="rounded-md p-1.5 hover:bg-secondary"
                  aria-label="Altre azioni"
                  data-testid="vetrina-actions-btn"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {showActions && (
                  <div className="absolute right-0 top-8 z-20 w-52 overflow-hidden rounded-md border border-border bg-card shadow-xl" data-testid="vetrina-actions-menu">
                    <ProfileShareMenuItems username={profile.username} testPrefix="vetrina" onAction={() => setShowActions(false)} />
                    {/* Phase F9.3 — Segnala Utente (distinct from Blocca) */}
                    <button
                      onClick={() => { setReportOpen(true); setShowActions(false); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold hover:bg-muted"
                      data-testid="vetrina-report-btn"
                    >
                      <Flag className="h-4 w-4" /> Segnala
                    </button>
                    <button
                      onClick={() => { onToggleBlock(); setShowActions(false); }}
                      disabled={blockBusy}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold hover:bg-muted disabled:opacity-50 ${
                        blockStatus.i_blocked_them ? 'text-emerald-600' : 'text-red-600'
                      }`}
                      data-testid={blockStatus.i_blocked_them ? 'vetrina-unblock-btn' : 'vetrina-block-btn'}
                    >
                      {blockStatus.i_blocked_them
                        ? (<><ShieldOff className="h-4 w-4" /> Sblocca</>)
                        : (<><Shield className="h-4 w-4" /> Blocca</>)}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* "Argomenti trattati · Principale · secondarie" — same row as the People card */}
          {categoryName && (
            <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid="vetrina-topics-row">
              <Tag className="h-3.5 w-3.5 shrink-0 gold-text" strokeWidth={2} />
              <span className="text-muted-foreground">{t('people.topics_label', { ns: 'home' })}</span>
              <span className="font-semibold gold-text" data-testid="vetrina-category">{categoryName}</span>
              {secondaryCats.map((c) => (
                <span key={c.slug} className="gold-text" data-testid={`vetrina-category-secondary-${c.slug}`}>· {c.name}</span>
              ))}
            </p>
          )}
          {/* lingua / argomenti — dati esistenti, nessuna nuova tassonomia */}
          {(genderText || profile.topics?.length > 0 || profile.languages?.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="vetrina-chips">
              {genderText && (
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold" data-testid="vetrina-gender">
                  {genderText}
                </span>
              )}
              {profile.languages?.length > 0 && (
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold" title={profile.languages.map(langLabel).join(', ')} data-testid="vetrina-languages">
                  {profile.languages.map(langFlag).join(' ')}
                </span>
              )}
              {(profile.topics || []).slice(0, 5).map((topic) => (
                <span key={topic} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[11px] font-semibold gold-text">
                  {topic}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* ── BIO (users.bio, forma estesa) ─────────────────────── */}
      {profile.bio && (
        <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90" data-testid="vetrina-bio">
          {profile.bio}
        </p>
      )}

      {/* ── 🎧 ASCOLTA LA MIA VOCE (player esistente, NO autoplay) ─ */}
      {profile.audio_avatar_url && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3" data-testid="vetrina-listen-voice">
          <AudioAvatarPlayer src={profile.audio_avatar_url} size="md" />
          <div>
            <p className="flex items-center gap-1.5 text-sm font-bold gold-text">
              <Headphones className="h-4 w-4" /> Ascolta la mia voce
            </p>
            <p className="text-[11px] text-muted-foreground">La presentazione vocale di @{profile.username}</p>
          </div>
        </div>
      )}

      {/* ── LIVE ORA — priorità massima, CTA ENTRA ────────────── */}
      {liveNow && !locked && (
        <button
          onClick={() => navigate(`/lives/${liveNow.id}`)}
          className="mt-4 flex w-full items-center justify-between rounded-xl bg-red-500 px-4 py-3 text-white transition-colors hover:bg-red-600"
          data-testid="vetrina-enter-live-btn"
        >
          <span className="flex min-w-0 items-center gap-2 text-sm font-bold">
            <Radio className="h-4 w-4 animate-pulse" />
            <span className="truncate">{liveNow.title || 'Live in corso'}</span>
          </span>
          <span className="shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase">Entra</span>
        </button>
      )}

      {/* ── CTA PRINCIPALI ────────────────────────────────────── */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]" data-testid="vetrina-cta-row">
        <Button
          variant="secondary"
          onClick={toggleFav}
          className={`h-10 text-sm font-semibold ${profile.is_following ? '' : 'gold-gradient text-black'}`}
          data-testid="vetrina-fav-btn"
        >
          {profile.is_following ? '♥ Nei Preferiti' : '♡ Preferiti'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(`/messages?u=${encodeURIComponent(profile.username)}`)}
          className="h-10 text-sm font-semibold"
          data-testid="vetrina-message-btn"
        >
          <MessageCircle className="mr-1.5 h-4 w-4" /> Messaggio
        </Button>
        {isVocePro && !locked && !blockStatus.i_blocked_them && me?.id !== profile.id && callStatus?.creator_id === profile.id && (
          <CallAvailabilityButton
            available={callStatus.can_call}
            occupied={callStatus.accepting_calls && callStatus.state === 'busy'}
            onClick={scrollToO2O}
            className="h-10 text-sm font-semibold"
            testId="vetrina-call-btn"
          />
        )}
        {isVocePro && !locked && me && me.username !== profile.username && (
          <Button
            onClick={() => setTipOpen(true)}
            className="gold-gradient h-10 text-sm font-semibold text-black"
            data-testid="vetrina-tip-btn"
          >
            <Gift className="mr-1.5 h-4 w-4" /> Tip
          </Button>
        )}
      </div>

      {/* ── PROFILO PRIVATO — gating esistente INVARIATO ──────── */}
      {!locked && !blockStatus.i_blocked_them && <ProfileLiveSchedule key={profile.id} lives={lives} />}
      {locked ? (
        <div className="mt-10 border-t border-border pb-10 pt-10 text-center" data-testid="private-profile-lock">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-amber-400">
            <Lock className="h-7 w-7 gold-text" strokeWidth={1.6} />
          </div>
          <h3 className="text-xl font-bold">Questo profilo è privato</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Solo i membri approvati da @{profile.username} possono vedere racconti, live e contenuti.
          </p>
          {accessPrice > 0 && (
            <p className="mt-3 text-sm font-semibold gold-text" data-testid="private-profile-lock-price">
              Accesso: {accessPrice} Crediti (pagamento una tantum)
            </p>
          )}
          <div className="mt-6">
            {accessInfo?.my_status === 'pending' ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-amber-300" data-testid="private-profile-pending-note">
                  Richiesta inviata — in attesa di approvazione
                </p>
                <Button onClick={onCancelRequest} disabled={accessBusy} variant="secondary" data-testid="private-profile-cancel-btn">
                  Annulla richiesta{accessPrice > 0 ? ' (crediti restituiti)' : ''}
                </Button>
              </div>
            ) : (
              <Button
                onClick={onRequestAccess}
                disabled={accessBusy}
                className="gold-gradient rounded-full px-6 font-bold text-black"
                data-testid="private-profile-request-btn"
              >
                <Lock className="mr-1.5 h-4 w-4" />
                Richiedi accesso{accessPrice > 0 ? ` · ${accessPrice} Crediti` : ''}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ── MENU VOICE-FIRST: 🎙 Racconti Audio | 📖 Racconti Testo |
              🔴 Live | 🏷 Categorie — legacy photo/video posts are NOT
              presented here (data untouched in DB/storage). */}
          <div className="mt-8">
            <VoiceTabsBar active={tab} onChange={setTab} testPrefix="vetrina-tab" />
          </div>

          {tab === 'audio' && (
            <RaccontiSection
              posts={posts}
              types={['audio']}
              title={null}
              username={profile.username}
              onOpen={(p) => navigate(`/post/${p.id}`)}
              emptyText={`@${profile.username} non ha ancora pubblicato racconti audio.`}
              testId="vetrina-racconti-audio"
            />
          )}

          {tab === 'text' && (
            <RaccontiSection
              posts={posts}
              types={['text']}
              title={null}
              username={profile.username}
              onOpen={(p) => navigate(`/post/${p.id}`)}
              emptyText={`@${profile.username} non ha ancora pubblicato racconti testuali.`}
              testId="vetrina-racconti-text"
            />
          )}

          {tab === 'live' && (
            (liveNow || scheduledLives.length > 0 || pastLives.length > 0) ? (
              <VetrinaLiveSection
                liveNow={liveNow}
                scheduledLives={scheduledLives}
                pastLives={pastLives}
                onChange={onLiveChange}
              />
            ) : (
              <p className="mt-6 rounded-xl border border-border bg-card px-4 py-6 text-center text-xs text-muted-foreground" data-testid="vetrina-live-empty">
                @{profile.username} non ha Live al momento.
              </p>
            )
          )}

          {tab === 'categories' && (
            <div className="mt-6" data-testid="vetrina-categories-panel">
              <CategoriesTab
                username={profile.username}
                categorySlug={profile.category_slug}
                categoryName={categoryName}
                secondaryCategories={secondaryCats}
                topics={profile.topics || []}
                lives={lives}
              />
            </div>
          )}

          {/* ── ☎️ ONE-TO-ONE (CallCreatorSection esistente) ────── */}
          {isVocePro && !blockStatus.i_blocked_them && profile?.id && (
            <section ref={o2oRef} className="mt-8 scroll-mt-20" data-testid="vetrina-o2o-section">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider gold-text">
                <Phone className="h-4 w-4" /> Parla con @{profile.username}
              </h2>
              <CallCreatorSection
                creatorId={profile.id}
                username={profile.username}
                onStatusChange={setCallStatus}
                unavailableFallback={(
                  <p className="rounded-xl border border-border bg-card px-4 py-4 text-center text-xs text-muted-foreground" data-testid="vetrina-o2o-unavailable">
                    Le chiamate One-to-One non sono disponibili al momento.
                  </p>
                )}
              />
            </section>
          )}
        </>
      )}

      {/* 🎁 Tip dal profilo — RIUSA il sistema Tip chat F4/F4.1/F4.2
          (preset Admin, wallet, ledger, enforcement Voce Pro, client_ref) */}
      <ChatTipDialog
        open={tipOpen}
        onOpenChange={setTipOpen}
        username={profile.username}
        t={t}
        onSent={() => {}}
      />

      {/* Phase F9.3 — Segnala Utente dalla Vetrina (stesso sistema della chat) */}
      {me && me.username !== profile.username && (
        <ReportUserDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          username={profile.username}
          testIdPrefix="vetrina-report"
        />
      )}
    </div>
  );
};

export default VetrinaProfile;
